import React from "react";
import moment from "moment";
import { useState } from "react";
import { useEffect } from "react";
import { groupBy, union, values } from "lodash";
import styles from "../../../../../../styles/Campaigns/CampaignLogs.module.scss";
import { getUserTimeZone } from "../../../../../../utils";
import { formatValidityPeriod } from "../../../../../../utils/formatValidity";
import InfoBlock from "../../../../../common/InfoBlock";
import { commonConfig, fillMissingValidityWithOldValues } from "../utils";
import { LogRenderContext } from "../LogRenderer";
import SecondaryTable from "../../../../../common/SecondaryTable";
import { getTimesOfDay } from "../../../../../../api";
import { DAY_OF_WEEK } from "../../../../../../constants";
import trackingElement from "../../../../../common/TrackingElement";
import { covertPositionNumberToChar } from "../../../../../../utils/covertPositionNumberToChar";
// Media Details Panel Component
const MediaDetailsPanel = () => {
  const { log } = React.useContext(LogRenderContext);
  const { medias } = log.dataSnapshot;
  const mediasNew = log.changedFields?.medias || [];

  if (!medias || medias.length === 0) {
    return (
      <div className={styles.noDataContainer}>
        <div>No media found</div>
      </div>
    );
  }

  return (
    <div>
      {medias.map((media) => {
        const mediaNew = mediasNew?.find((m) => m.id === media.id);

        return (
          <div key={media.id} className={styles.mediaContainer}>
            <div className={styles.mediaHeader}>{media.name}</div>
            <MediaInfoBlock media={media} mediaNew={mediaNew} />
          </div>
        );
      })}
    </div>
  );
};
const MediaInfoBlock = ({ media, mediaNew }) => {
  const { campaignId } = React.useContext(LogRenderContext);
  const [timesOfDay, setTimesOfDay] = useState([]);

  const modifiedPlaysPerDay =
    media?.placements?.[0]?.plays?.map((play) => ({
      [DAY_OF_WEEK[play.dayOfWeek].label]: play.numberOfPlays,
      timeOfDayId: play.timeOfDayId,
    })) ?? [];
  const modifiedPlaysPerDayNew =
    mediaNew?.placements?.[0]?.plays.map((play) => ({
      [DAY_OF_WEEK[play.dayOfWeek].label]: play.numberOfPlays,
      timeOfDayId: play.timeOfDayId,
    })) ?? [];

  const timesOfdayUnion = union(
    modifiedPlaysPerDay.map((play) => play.timeOfDayId),
    modifiedPlaysPerDayNew.map((play) => play.timeOfDayId)
  );

  const plays = values(groupBy(modifiedPlaysPerDay, "timeOfDayId")).map((play) => {
    return play.reduce((merge, value) => {
      return {
        ...merge,
        ...value,
      };
    }, {});
  });
  const playsNew = values(groupBy(modifiedPlaysPerDayNew, "timeOfDayId")).map((play) => {
    return play.reduce((merge, value) => {
      return {
        ...merge,
        ...value,
      };
    }, {});
  });
  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "timeOfDayId"];
  const playsWithDiff = timesOfdayUnion.map((td) => {
    const play = plays.filter((p) => p.timeOfDayId === td)?.[0] || {};
    const playNew = playsNew.filter((p) => p.timeOfDayId === td)?.[0] || {};
    const playWithDiff = weekDays.reduce((diff, key) => {
      if (key === "timeOfDayId") {
        diff["timeOfDayId"] = play.timeOfDayId || playNew.timeOfDayId;
        return diff;
      } // Skip timeOfDayId as it is not needed in diff
      diff[key] = { old: play?.[key], new: playNew?.[key] };
      return diff;
    }, {});
    return playWithDiff;
  });

  const formatCategories = (categories) => {
    if (!categories || categories.length === 0) return undefined;

    return categories.map((cat) => cat.categoryName).join(", ");
  };

  useEffect(() => {
    const fetchTimesOfDay = async () => {
      try {
        const sort = {
          campaignId: campaignId,
          action: "list",
        };
        const response = await getTimesOfDay(sort);
        setTimesOfDay(response.data);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error fetching times of day:", error);
      }
    };

    fetchTimesOfDay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <InfoBlock
        header={"Media Details"}
        className={styles.changeTracking}
        config={commonConfig([
          {
            title: "Media Name & ID",
            value: {
              old: media?.name ? `${media?.name} (${media?.id?.substring(0, 8)})` : undefined,
              new: mediaNew?.name
                ? `${mediaNew?.name} (${mediaNew?.id?.substring(0, 8)})`
                : undefined,
            },
          },
          {
            title: "Target Group",
            value: {
              old: media?.targetGroupName,
              new: mediaNew?.targetGroupName,
            },
          },
          {
            title: `Validity (${getUserTimeZone().abbr})`,
            value: {
              old: formatValidityPeriod(media?.validity),
              new: formatValidityPeriod(
                fillMissingValidityWithOldValues(media?.validity, mediaNew?.validity)
              ),
            },
          },
          {
            title: "Content",
            value: {
              old: media?.contentName,
              new: mediaNew?.contentName,
            },
          },
          {
            title: "Content Type",
            value: {
              old: media?.contentTypeName,
              new: mediaNew?.contentTypeName,
            },
          },
          {
            title: "Duration",
            value: {
              old: media?.contentDurationInSeconds
                ? `${media.contentDurationInSeconds} seconds`
                : media?.contentDurationInSeconds,
              new: mediaNew?.contentDurationInSeconds
                ? `${mediaNew.contentDurationInSeconds} seconds`
                : mediaNew?.contentDurationInSeconds,
            },
          },
          {
            title: "Category",
            value: {
              old: formatCategories(media?.categories),
              new: formatCategories(mediaNew?.categories),
            },
          },
          {
            title: "Brand",
            value: {
              old: media?.brandName,
              new: mediaNew?.brandName,
            },
          },
          {
            title: "Schedule Number",
            value: {
              old: media?.scheduleNumber,
              new: mediaNew?.scheduleNumber,
            },
          },
        ])}
      />
      <InfoBlock
        header="Placement & Number Of Plays"
        className={styles.changeTracking}
        config={commonConfig([
          // {
          //   title: "Brand",
          //   value: {
          //     old: `${media?.brandName} (${media?.brandType})`,
          //     new: mediaNew ? `${mediaNew?.brandName} (${mediaNew?.brandType})` : undefined,
          //   },
          // },
          // {
          //   title: "Placement & Number of Plays",
          //   value: {
          //     old: formatPlacementDetails(media?.placements),
          //     new: formatPlacementDetails(mediaNew?.placements),
          //   },
          // },
          {
            title: "Playlist Pack Type",
            value: {
              old: media?.placements?.[0]?.playlistPackTypeName,
              new: mediaNew?.placements?.[0]?.playlistPackTypeName,
            },
          },
          {
            title: "Segment",
            value: {
              old: media?.placements?.[0]?.segmentName,
              new: mediaNew?.placements?.[0]?.segmentName,
            },
          },
          {
            title: "Position",
            value: {
              old:
                media?.placements?.[0]?.position !== undefined
                  ? covertPositionNumberToChar(media.placements[0].position)
                  : undefined,
              new:
                mediaNew?.placements?.[0]?.position !== undefined
                  ? covertPositionNumberToChar(mediaNew.placements[0].position)
                  : undefined,
            },
          },
          {
            title: "Mode",
            value: {
              old: media?.placementMode,
              new: mediaNew?.placementMode,
            },
          },
          {
            title: "Weekly Play Count",
            value: {
              old: media?.weeklyPlayCount,
              new: mediaNew?.weeklyPlayCount,
            },
          },
          {
            title: "Show Play Count",
            value: {
              old: media?.showPlayCount,
              new: mediaNew?.showPlayCount,
            },
          },
          {
            title: "Play Back To Back",
            value: {
              old:
                media?.canPlayBackToBack !== undefined
                  ? media?.canPlayBackToBack
                    ? "True"
                    : "False"
                  : undefined,
              new:
                mediaNew?.canPlayBackToBack !== undefined
                  ? mediaNew.canPlayBackToBack
                    ? "True"
                    : "False"
                  : undefined,
            },
          },
          {
            title: "Billable Weekly Play Count",
            value: {
              old: media?.billableWeeklyPlayCount,
              new: mediaNew?.billableWeeklyPlayCount,
            },
          },
          // {
          //   title: "Created By",
          //   size: {
          //     lg: 12,
          //   },
          //   value: {
          //     old: media?.createdBy
          //       ? `${media.createdBy.firstName} ${media.createdBy.lastName} (${media.createdBy.email})`
          //       : "Unknown",
          //     new: mediaNew?.createdBy
          //       ? `${mediaNew.createdBy.firstName} ${mediaNew.createdBy.lastName} (${mediaNew.createdBy.email})`
          //       : undefined,
          //   },
          // },
          {
            title: "",
            size: {
              lg: 12,
              md: 12,
              sm: 12,
            },
            value: {
              old:
                media.placementMode === "Advanced" || mediaNew?.placementMode === "Advanced" ? (
                  <SecondaryTable
                    data={playsWithDiff}
                    columns={[
                      {
                        id: "timeOfDay",
                        Header: "",
                        accessor: (d) => {
                          const timeOfDay = timesOfDay.find((day) => day.id === d.timeOfDayId);
                          return timeOfDay ? (
                            <div style={{ display: "flex", flexDirection: "column" }}>
                              <div className={styles.placementsTimeName}>{timeOfDay.name}</div>
                              <div style={{ fontSize: ".7rem", color: "#666" }}>
                                {moment(timeOfDay.startTime, "HH:mm:ss").format("hh:mm a")}-
                                {moment(timeOfDay.endTime, "HH:mm:ss").format("hh:mm a")}
                              </div>
                            </div>
                          ) : (
                            ""
                          );
                        },
                        width: 128,
                      },
                      {
                        id: "mon",
                        Header: "Mon",
                        accessor: (d) => trackingElement(d.Mon) || 0,
                        width: 65,
                      },
                      {
                        id: "tue",
                        Header: "Tue",
                        accessor: (d) => trackingElement(d.Tue) || 0,
                        width: 65,
                      },
                      {
                        id: "wed",
                        Header: "Wed",
                        accessor: (d) => trackingElement(d.Wed) || 0,
                        width: 65,
                      },
                      {
                        id: "thu",
                        Header: "Thu",
                        accessor: (d) => trackingElement(d.Thu) || 0,
                        width: 65,
                      },
                      {
                        id: "fri",
                        Header: "Fri",
                        accessor: (d) => trackingElement(d.Fri) || 0,
                        width: 65,
                      },
                      {
                        id: "sat",
                        Header: "Sat",
                        accessor: (d) => trackingElement(d.Sat) || 0,
                        width: 65,
                      },
                      {
                        id: "sun",
                        Header: "Sun",
                        accessor: (d) => trackingElement(d.Sun) || 0,
                        width: 65,
                      },
                    ]}
                    bodyColumnClass={styles.placementsBodyHeader}
                  />
                ) : undefined,
              new: undefined,
            },
          },
        ])}
      />
    </>
  );
};
export default MediaDetailsPanel;
export { MediaInfoBlock };
